import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import { PartActionResponse, PartActionTypes } from '../@types/partAction.type';
import {
  createBulkPartActions,
  createPartAction,
  deletePartActionById,
  getAllPartActionTypes,
  getPartActionByServiceId,
  updatePartActionById,
} from '../api/partAction';
import { PartActionKey, PartActionTypesKey } from '../configs/partAction';
import { ServiceLogAllKey } from '../configs/services';

export const useAddPartAction = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createPartAction(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
    },
  });
};

export const useGetPartActionByServiceId = (id: number): UseQueryResult<PartActionResponse[]> => {
  return useQuery({
    queryKey: [PartActionKey, id],
    queryFn: () => getPartActionByServiceId(id),
    enabled: id !== 0,
  });
};

export const useUpdatePartAction = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      await updatePartActionById(data.id, data.data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
    },
  });
};

export const useDeletePartActionById = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await deletePartActionById(data.id);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartActionKey, id],
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id],
      });
    },
  });
};

export const useGetPartActionTypes = (): UseQueryResult<PartActionTypes[]> => {
  return useQuery({
    queryKey: [PartActionTypesKey],
    queryFn: () => getAllPartActionTypes(),
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 4 * 60 * 60 * 1000,
    enabled: false,
  });
};

export const useAddBulkPartActions = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createBulkPartActions(serviceId, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId],
      });
    },
  });
};
